package one.healthium.profile.ui.profile

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.material3.rememberTopAppBarState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.input.nestedscroll.nestedScroll
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.util.fastMaxOfOrNull
import loco.GENERAL_BMI
import loco.GENERAL_DETAILS
import loco.ME_BMI_GOAL_VALUE_FORMAT
import loco.ME_CALORIES_HISTORY_SECTION
import loco.ME_DAYS_IN_A_ROW_LABEL
import loco.ME_DAYS_IN_A_ROW_VALUE
import loco.ME_WEIGHT_HISTORY_SECTION
import loco.ME_WEIGHT_LOSS_PROGRESS_SECTION
import loco.ME_WEIGHT_LOSS_PROGRESS_VALUE_FORMAT
import one.healthium.base.utils.Formatters.formatCalories
import one.healthium.base.utils.Formatters.formatWeight
import one.healthium.base.utils.toWeightString
import one.healthium.loco.LString
import one.healthium.loco.resolve
import one.healthium.profile.ui.profile.components.ProfileAppBar
import one.healthium.ui.compose.components.AppIcons
import one.healthium.ui.compose.components.AppLinearProgressIndicator
import one.healthium.ui.compose.components.AppLoadingScreen
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.components.AppTextButton
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.orange_2
import one.healthium.ui.compose.theme.white
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import kotlin.math.max

interface ProfileEventDelegate {
    fun onSettingsClicked()
    fun onWeightProgressClicked()
    fun onCaloriesProgressClicked()
    fun onHealthKitClicked()
}

@Composable
fun ProfileScreen(eventDelegate: ProfileEventDelegate) {
    val viewModel = koinViewModel<ProfileViewModel>()
    val state by viewModel.state.collectAsState()

    ProfileScreenContent(
        state = state,
        onSettingsClicked = eventDelegate::onSettingsClicked,
        onWeightProgressClicked = eventDelegate::onWeightProgressClicked,
        onCaloriesProgressClicked = eventDelegate::onCaloriesProgressClicked,
    )
}

@Composable
internal fun ProfileScreenContent(
    state: ProfileUiState,
    onSettingsClicked: () -> Unit,
    onWeightProgressClicked: () -> Unit,
    onCaloriesProgressClicked: () -> Unit,
) {
    val topAppBarState = rememberTopAppBarState()
    val scrollBehavior = TopAppBarDefaults.exitUntilCollapsedScrollBehavior(
        state = topAppBarState,
        snapAnimationSpec = null,
    )

    AppScaffold(
        modifier = Modifier.testTag("profile_screen"),
        topBar = {
            ProfileAppBar(
                profileState = state,
                topAppBarState = topAppBarState,
                scrollBehavior = scrollBehavior,
                onSettingsClicked = onSettingsClicked,
            )
        }
    ) {
        Box(modifier = Modifier.padding(it)) {
            Profile(
                modifier = Modifier.nestedScroll(scrollBehavior.nestedScrollConnection),
                profile = state.profile,
                onWeightProgressClicked = onWeightProgressClicked,
                onCaloriesProgressClicked = onCaloriesProgressClicked,
            )

            AppLoadingScreen(visible = state.loading)
        }
    }
}

@Composable
private fun Profile(
    modifier: Modifier = Modifier,
    profile: ProfileUiModel?,
    onWeightProgressClicked: () -> Unit,
    onCaloriesProgressClicked: () -> Unit,
) {
    if (profile == null) return

    Column(
        modifier = modifier
            .fillMaxWidth()
            .verticalScroll(rememberScrollState())
            .padding(horizontal = 12.dp, vertical = 24.dp),
    ) {
        WeightLossProgress(profile)
        Spacer(Modifier.height(24.dp))
        DaysAndBmi(profile)
        Spacer(Modifier.height(24.dp))
        if (profile.weightHistory.isNotEmpty()) {
            TitleWithDetailsButton(
                text = stringResource(loco.Res.string.ME_WEIGHT_HISTORY_SECTION),
                onClick = onWeightProgressClicked,
            )
            Chart(
                items = remember(profile.weightHistory) {
                    profile.weightHistory.map { it.dateRelativeShort to it.value }
                },
                valueFormater = { it.toWeightString(profile.isMetric) },
                barColor = AppTheme.profileStyle.weightProgressChartStyle.barColor,
                barColorHighlighted = AppTheme.profileStyle.weightProgressChartStyle.highlightedBarColor,
                barShape = AppTheme.profileStyle.weightProgressChartStyle.barShapeOrDefault,
                cardShape = AppTheme.profileStyle.weightProgressChartStyle.cardShapeOrDefault,
            )
        }
        Spacer(Modifier.height(24.dp))
        if (profile.caloriesHistory.isNotEmpty()) {
            TitleWithDetailsButton(
                text = stringResource(loco.Res.string.ME_CALORIES_HISTORY_SECTION),
                onClick = onCaloriesProgressClicked,
            )
            Chart(
                items = remember(profile.caloriesHistory) { profile.caloriesHistory.map { it.date to it.value } },
                valueFormater = { it.formatCalories(addUnit = false, roundToK = true).resolve() },
                barColor = AppTheme.profileStyle.caloriesChartStyle.barColor,
                barColorHighlighted = AppTheme.profileStyle.caloriesChartStyle.highlightedBarColor,
                barShape = AppTheme.profileStyle.caloriesChartStyle.barShapeOrDefault,
                cardShape = AppTheme.profileStyle.caloriesChartStyle.cardShapeOrDefault,
            )
        }
        Spacer(Modifier.height(80.dp))
    }
}

@Composable
private fun TitleWithDetailsButton(
    text: String,
    onClick: () -> Unit,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            modifier = Modifier.weight(1f),
            text = text,
            style = AppTheme.textStyles.section,
            color = AppTheme.colors.text400,
        )
        AppTextButton(
            onClick = onClick,
        ) {
            Text(stringResource(loco.Res.string.GENERAL_DETAILS))
            Spacer(Modifier.width(8.dp))
            Icon(
                modifier = Modifier.size(width = 8.dp, height = 13.dp),
                painter = painterResource(AppIcons.icArrowRight),
                contentDescription = null,
            )
        }
    }
}

@Composable
private fun ColumnScope.WeightLossProgress(profile: ProfileUiModel) {
    val animatedProgress by animateFloatAsState(
        targetValue = if (profile.weightLossProgress > 0) max(profile.weightLossProgress, 0.03f) else 0f,
        animationSpec = tween(durationMillis = 500),
    )

    Row(
        modifier = Modifier.fillMaxWidth()
    ) {
        Text(
            modifier = Modifier.weight(1f),
            text = stringResource(loco.Res.string.ME_WEIGHT_LOSS_PROGRESS_SECTION),
            style = AppTheme.textStyles.section,
            color = AppTheme.colors.text400,
        )
        Text(
            text = stringResource(
                loco.Res.string.ME_WEIGHT_LOSS_PROGRESS_VALUE_FORMAT,
                profile.weightLossLeft.formatWeight(profile.isMetric).resolve(),
            ),
            style = AppTheme.textStyles.secondary_17_500,
            color = AppTheme.colors.text400,
        )
    }
    Spacer(Modifier.height(12.dp))
    AppLinearProgressIndicator(
        modifier = Modifier.fillMaxWidth(),
        color = AppTheme.profileStyle.weightProgressBarStyle?.highlightedBarColor ?: orange_2,
        trackColor = AppTheme.profileStyle.weightProgressBarStyle?.barColor ?: white,
        progress = { animatedProgress },
    )
}

@Composable
private fun DaysAndBmi(profile: ProfileUiModel) {
    Row(
        modifier = Modifier.fillMaxWidth()
    ) {
        LabelAndCard(
            modifier = Modifier.weight(1f),
            label = stringResource(loco.Res.string.ME_DAYS_IN_A_ROW_LABEL),
            primaryText = profile.visitsInRow.toString(),
            secondaryText = stringResource(loco.Res.string.ME_DAYS_IN_A_ROW_VALUE),
        )
        Spacer(Modifier.width(7.dp))
        LabelAndCard(
            modifier = Modifier.weight(1f),
            label = stringResource(loco.Res.string.GENERAL_BMI),
            primaryText = "${profile.currentBmi}",
            secondaryText = stringResource(loco.Res.string.ME_BMI_GOAL_VALUE_FORMAT, profile.targetBmi),
        )
    }
}

@Composable
private fun LabelAndCard(
    modifier: Modifier = Modifier,
    label: String,
    primaryText: String,
    secondaryText: String,
) {
    Column(
        modifier = modifier,
    ) {
        Text(
            text = label,
            style = AppTheme.textStyles.section,
            color = AppTheme.colors.text400,
        )
        Spacer(Modifier.height(12.dp))
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .heightIn(min = 60.dp)
                .background(white, AppTheme.profileStyle.cardShapeOrDefault)
                .padding(horizontal = 24.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                modifier = Modifier.weight(1f),
                text = primaryText,
                style = AppTheme.textStyles.poppins26Bold,
                color = AppTheme.colors.text100,
            )
            Text(
                text = secondaryText,
                style = AppTheme.textStyles.secondary_17_400,
                color = AppTheme.colors.text100.copy(alpha = 0.3f),
            )
        }
    }
}

@Composable
private fun ColumnScope.Chart(
    items: List<Pair<LString, Float>>,
    valueFormater: @Composable (Float) -> String = { it.toString() },
    barColor: Color,
    barColorHighlighted: Color,
    barShape: Shape,
    cardShape: Shape,
) {
    if (items.isEmpty()) return

    val max = remember(items) { items.fastMaxOfOrNull { it.second } ?: 1f }
    val chartHeight = 241.dp

    LazyRow(
        modifier = Modifier
            .fillMaxWidth()
            .height(chartHeight)
            .background(white, cardShape),
        reverseLayout = true,
        contentPadding = PaddingValues(
            top = 16.dp, start = 16.dp, end = 16.dp, bottom = 24.dp
        )
    ) {
        items(items.size) { index ->
            ChartBar(
                height = (items[index].second / max.coerceAtLeast(1f)),
                label = items[index].first.resolve(),
                value = valueFormater(items[index].second),
                color = if (index == 0) barColorHighlighted else barColor,
                shape = barShape,
            )
        }
    }
}

@Composable
private fun ChartBar(
    height: Float,
    label: String,
    value: String,
    color: Color,
    shape: Shape,
) {
    Column(
        modifier = Modifier
            .width(60.dp)
            .fillMaxHeight(),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Bottom,
    ) {
        val additionalWeight = 1f - height
        if (additionalWeight > 0) {
            Spacer(Modifier.weight(additionalWeight))
        }
        Text(
            text = value,
            style = AppTheme.textStyles.poppins18Medium,
            color = AppTheme.colors.text100,
        )
        if (height > 0f) {
            Box(
                modifier = Modifier
                    .width(43.dp)
                    .weight(height)
                    .background(color = color, shape = shape)
                    .clip(shape)
            )
        }
        Spacer(Modifier.height(9.dp))
        Text(
            text = label,
            style = AppTheme.textStyles.secondary_12_400,
            color = AppTheme.colors.text300,
            textAlign = TextAlign.Center,
        )
    }
}